/**
 * Account Queries
 *
 * Pure lookup/query functions for accounts and relationships.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import { Account, LearnerAccount, ParentAccount, TeacherAccount, AccountsState, isLearnerAccount, isParentAccount, isTeacherAccount } from '../types';

/**
 * Get active account from accounts state
 */
export function getActiveAccount(accountsState: AccountsState): Account | null {
  return accountsState.accountsById[accountsState.activeAccountId] || null;
}

/**
 * Get account by ID
 */
export function getAccountById(accountsState: AccountsState, accountId: string): Account | null {
  return accountsState.accountsById[accountId] || null;
}

/**
 * Switch active account
 */
export function switchActiveAccount(accountsState: AccountsState, newAccountId: string): AccountsState {
  const account = accountsState.accountsById[newAccountId];
  if (!account) {
    return accountsState;
  }
  return {
    ...accountsState,
    activeAccountId: newAccountId,
    accountsById: {
      ...accountsState.accountsById,
      [newAccountId]: {
        ...account,
        lastActive: Date.now()
      }
    }
  };
}

/**
 * Get all learners linked to a parent or teacher
 */
export function getLinkedLearners(accountsState: AccountsState, parentOrTeacherId: string): LearnerAccount[] {
  const account = accountsState.accountsById[parentOrTeacherId];
  if (!account) return [];
  let learnerIds: string[] = [];
  if (account.role === 'parent') {
    learnerIds = accountsState.relationships.parentToLearners[parentOrTeacherId] || [];
  } else if (account.role === 'teacher') {
    learnerIds = accountsState.relationships.teacherToLearners[parentOrTeacherId] || [];
  }
  return learnerIds.map(id => accountsState.accountsById[id]).filter((account): account is LearnerAccount => account ? isLearnerAccount(account) : false);
}

/**
 * Get all parents linked to a learner
 */
export function getLinkedParents(accountsState: AccountsState, learnerId: string): ParentAccount[] {
  const parentIds = accountsState.relationships.learnerToParents[learnerId] || [];
  return parentIds.map(id => accountsState.accountsById[id]).filter((account): account is ParentAccount => account ? isParentAccount(account) : false);
}

/**
 * Get learner account for active context
 * - If active account is learner: return it
 * - If active account is parent/teacher: return first linked learner
 */
export function getContextLearnerAccount(accountsState: AccountsState): LearnerAccount | null {
  const activeAccount = getActiveAccount(accountsState);
  if (!activeAccount) return null;
  if (isLearnerAccount(activeAccount)) {
    return activeAccount;
  }
  if (isParentAccount(activeAccount)) {
    const linkedLearners = getLinkedLearners(accountsState, activeAccount.id);
    return linkedLearners[0] || null;
  }

  // Teacher: return first linked learner
  const learnerIds = accountsState.relationships.teacherToLearners[activeAccount.id] || [];
  const firstLearnerId = learnerIds[0];
  if (!firstLearnerId) return null;
  const learner = accountsState.accountsById[firstLearnerId];
  return learner && isLearnerAccount(learner) ? learner : null;
}

/**
 * Validate accounts state structure.
 *
 * Now also validates the `classrooms` registry shape — previously this field
 * was unchecked and a malformed classrooms object could slip through to
 * downstream consumers. We perform a shallow shape check (id/teacherId/
 * learnerIds/joinCode presence) rather than a deep validation; that level
 * of detail belongs in a schema validator like Zod.
 */
export function validateAccountsState(state: unknown): state is AccountsState {
  if (!state || typeof state !== 'object') return false;
  const s = state as Record<string, unknown>;
  if (!s.accountsById || typeof s.accountsById !== 'object') return false;
  if (typeof s.activeAccountId !== 'string') return false;
  if (!s.relationships || typeof s.relationships !== 'object') return false;

  // Allow empty activeAccountId (set during logout) — account may not exist
  // but the state structure is still valid
  const accountsById = s.accountsById as Record<string, unknown>;
  if (s.activeAccountId && !accountsById[s.activeAccountId as string]) {
    // Non-empty activeAccountId must reference an existing account
    return false;
  }

  // Validate relationships structure
  const rel = s.relationships as Record<string, unknown>;
  if (!rel.parentToLearners || !rel.learnerToParents || !rel.teacherToLearners) return false;

  // Validate classrooms shape (shallow) — F-ACC-ARC-R2-VALIDATE-ACCOUNTS-STATE-INCOMPLETE
  if (s.classrooms !== undefined) {
    if (s.classrooms === null || typeof s.classrooms !== 'object') return false;
    const classrooms = s.classrooms as Record<string, unknown>;
    for (const [classroomId, classroom] of Object.entries(classrooms)) {
      if (!classroom || typeof classroom !== 'object') return false;
      const c = classroom as Record<string, unknown>;
      if (typeof c.id !== 'string' || c.id !== classroomId) return false;
      if (typeof c.teacherId !== 'string') return false;
      if (!Array.isArray(c.learnerIds)) return false;
      if (typeof c.joinCode !== 'string') return false;
    }
  }
  return true;
}

/**
 * Get all unique school names from teacher accounts.
 * Returns sorted, deduplicated list. Useful for school selection UIs.
 */
export function getUniqueSchoolNames(accountsState: AccountsState): string[] {
  const schools = new Set<string>();
  for (const account of Object.values(accountsState.accountsById)) {
    if (isTeacherAccount(account) && account.schoolName) {
      schools.add(account.schoolName);
    }
  }
  return Array.from(schools).sort();
}